import React from "react";

function SkeletonLine({ w = "100%", h = 10 }) {
  return <div className="fsb-skel-line" style={{ width: w, height: h }} />;
}

function SkeletonBlock({ className = "", style = {} }) {
  return <div className={`fsb-skel-block ${className}`.trim()} style={style} />;
}

function StoreHeader() {
  return (
    <header className="fsb-skel-header">
      <SkeletonBlock className="fsb-skel-logo" />
      <div className="fsb-skel-nav">
        <SkeletonLine w="48px" />
        <SkeletonLine w="56px" />
      </div>
      <div className="fsb-skel-header-icons">
        <SkeletonBlock className="fsb-skel-icon" />
        <SkeletonBlock className="fsb-skel-icon" />
      </div>
    </header>
  );
}

function WidgetSlot({ children, widthPercent, centered }) {
  return (
    <div
      className="fsb-skel-widget-slot"
      style={{
        width: `${widthPercent ?? 100}%`,
        maxWidth: "100%",
        margin: centered ? "0 auto" : undefined,
      }}
    >
      {children}
    </div>
  );
}

function ProductPageSkeleton({ children, widthPercent }) {
  return (
    <div className="fsb-skel-page fsb-skel-page--product">
      <StoreHeader />
      <div className="fsb-skel-product-main">
        <SkeletonBlock className="fsb-skel-product-image" />
        <div className="fsb-skel-product-info">
          <SkeletonLine w="120px" h={8} />
          <SkeletonLine w="85%" h={14} />
          <SkeletonLine w="40%" h={12} />
          <SkeletonBlock className="fsb-skel-btn fsb-skel-btn--primary" />
          <div className="fsb-skel-product-widget">
            <WidgetSlot widthPercent={widthPercent}>{children}</WidgetSlot>
          </div>
        </div>
      </div>
    </div>
  );
}

function CartPageSkeleton({ children, widthPercent }) {
  return (
    <div className="fsb-skel-page fsb-skel-page--cart">
      <StoreHeader />
      <div className="fsb-skel-cart-body">
        <WidgetSlot widthPercent={widthPercent} centered>{children}</WidgetSlot>
        <div className="fsb-skel-cart-head">
          <SkeletonLine w="100px" h={14} />
          <SkeletonLine w="120px" h={10} />
        </div>
        <div className="fsb-skel-cart-table">
          <div className="fsb-skel-cart-row fsb-skel-cart-row--head">
            <SkeletonLine w="60px" h={8} />
            <SkeletonLine w="50px" h={8} />
            <SkeletonLine w="40px" h={8} />
          </div>
          <div className="fsb-skel-cart-row">
            <div className="fsb-skel-cart-product">
              <SkeletonBlock className="fsb-skel-cart-thumb" />
              <SkeletonLine w="140px" />
            </div>
            <SkeletonBlock className="fsb-skel-qty" />
            <SkeletonLine w="48px" />
          </div>
        </div>
        <div className="fsb-skel-cart-footer">
          <SkeletonLine w="140px" h={12} />
          <SkeletonBlock className="fsb-skel-btn fsb-skel-btn--checkout" />
        </div>
      </div>
    </div>
  );
}

function HomePageSkeleton({ children, widthPercent }) {
  return (
    <div className="fsb-skel-page fsb-skel-page--home">
      <StoreHeader />
      <SkeletonBlock className="fsb-skel-hero" />
      <div className="fsb-skel-home-body">
        <WidgetSlot widthPercent={widthPercent} centered>{children}</WidgetSlot>
        <div className="fsb-skel-product-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="fsb-skel-grid-item">
              <SkeletonBlock className="fsb-skel-grid-img" />
              <SkeletonLine w="80%" />
              <SkeletonLine w="50%" h={8} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AnnouncementPageSkeleton({ children }) {
  return (
    <div className="fsb-skel-page fsb-skel-page--announcement">
      <div className="fsb-skel-announcement-bar">{children}</div>
      <StoreHeader />
      <SkeletonBlock className="fsb-skel-hero" />
      <div className="fsb-skel-home-body">
        <div className="fsb-skel-product-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="fsb-skel-grid-item">
              <SkeletonBlock className="fsb-skel-grid-img" />
              <SkeletonLine w="80%" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Storefront page skeleton with a slot for the shipping bar widget.
 * @param {string} placementType - product_page | cart_page | home_page | announcement_bar
 * @param {number} [widgetWidthPercent] - width for cart/home (40–100)
 */
export default function StorefrontPageSkeleton({ placementType, widgetWidthPercent = 100, children }) {
  switch (placementType) {
    case "cart_page":
      return <CartPageSkeleton widthPercent={widgetWidthPercent}>{children}</CartPageSkeleton>;
    case "home_page":
      return <HomePageSkeleton widthPercent={widgetWidthPercent}>{children}</HomePageSkeleton>;
    case "announcement_bar":
      return <AnnouncementPageSkeleton>{children}</AnnouncementPageSkeleton>;
    case "product_page":
    default:
      return <ProductPageSkeleton widthPercent={widgetWidthPercent}>{children}</ProductPageSkeleton>;
  }
}
